import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useClinic } from '@/contexts/ClinicContext'
import { billingService, BillingStatus, UsageEvent, UsageSummary, Invoice } from '@/services/billingService'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2, CreditCard, FileText, BarChart3, ExternalLink, Download } from 'lucide-react'
import { PaymentSetup } from './PaymentSetup'
import { UpdatePaymentMethodCard } from './UpdatePaymentMethodCard'

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Active' },
  pending_setup: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Setup Pending' },
  past_due: { bg: 'bg-red-50', text: 'text-red-700', label: 'Past Due' },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled' },
  trialing: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Trial' },
}

const EVENT_LABELS: Record<string, string> = {
  mdi_visit: 'MDI Visit',
  consulting_visit: 'Consulting Visit',
  lab_order: 'Lab Order',
  at_home_phlebotomy: 'At-Home Phlebotomy',
}

export const BillingDashboard: React.FC = () => {
  const { clinic } = useClinic()
  const [activeTab, setActiveTab] = useState('usage')
  const [showSetup, setShowSetup] = useState(false)
  const [showUpdateCard, setShowUpdateCard] = useState(false)
  const [skippedOptimistic, setSkippedOptimistic] = useState(false)

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery<BillingStatus | null>({
    queryKey: ['billingStatus', clinic?.id],
    queryFn: () => (clinic?.id ? billingService.getBillingStatus(clinic.id) : null),
    enabled: !!clinic?.id,
  })

  useEffect(() => {
    if (skippedOptimistic && status?.setupSkippedAt) setSkippedOptimistic(false)
  }, [skippedOptimistic, status?.setupSkippedAt])

  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['billingUsage', clinic?.id],
    queryFn: () => (clinic?.id ? billingService.getUsage(clinic.id) : null),
    enabled: !!clinic?.id,
  })

  const { data: invoices, isLoading: invoicesLoading } = useQuery<Invoice[]>({
    queryKey: ['billingInvoices', clinic?.id],
    queryFn: () => (clinic?.id ? billingService.getInvoices(clinic.id) : []),
    enabled: !!clinic?.id,
  })

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
      </div>
    )
  }

  if (showUpdateCard && clinic?.id && status?.hasSubscription) {
    return (
      <UpdatePaymentMethodCard
        clinicId={clinic.id}
        onSuccess={() => {
          setShowUpdateCard(false)
          refetchStatus()
        }}
        onCancel={() => setShowUpdateCard(false)}
      />
    )
  }

  const isCancelled = status?.billingStatus === 'cancelled'

  // Show PaymentSetup when: user clicked "Set Up / Reactivate Billing" OR (pending_setup AND never skipped)
  const needsSetup = !status?.hasSubscription && status?.billingStatus === 'pending_setup'
  const hasSkipped = !!status?.setupSkippedAt || skippedOptimistic
  const shouldShowPaymentSetup = showSetup || (needsSetup && !hasSkipped)

  if (shouldShowPaymentSetup) {
    return (
      <PaymentSetup
        isReactivation={isCancelled}
        onSuccess={() => {
          setShowSetup(false)
          refetchStatus()
        }}
        onSkip={isCancelled ? undefined : () => {
          setShowSetup(false)
          setSkippedOptimistic(true)
          refetchStatus()
        }}
      />
    )
  }

  const statusStyle = STATUS_STYLES[status?.billingStatus || 'pending_setup']

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <Card className="border-slate-200">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${statusStyle.bg}`}>
              <CreditCard className={`h-5 w-5 ${statusStyle.text}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">Billing Status</h3>
                <Badge className={`${statusStyle.bg} ${statusStyle.text}`}>{statusStyle.label}</Badge>
              </div>
              {status?.paymentMethod && (
                <p className="text-sm text-slate-500">
                  {status.paymentMethod.brand} ending in {status.paymentMethod.last4}
                </p>
              )}
            </div>
          </div>
          {(status?.billingStatus === 'pending_setup' || status?.billingStatus === 'cancelled') && (
            <Button onClick={() => setShowSetup(true)} className="bg-brand-teal hover:bg-brand-teal/90">
              {status?.billingStatus === 'cancelled' ? 'Reactivate Billing' : 'Set Up Billing'}
            </Button>
          )}
          {status?.unbilledCount ? (
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900">${status.unbilledTotal.toFixed(2)}</p>
              <p className="text-xs text-slate-500">{status.unbilledCount} unbilled</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="usage" className="text-xs sm:text-sm">
            <BarChart3 className="mr-1.5 h-4 w-4" />
            Current Period
          </TabsTrigger>
          <TabsTrigger value="payment" className="text-xs sm:text-sm">
            <CreditCard className="mr-1.5 h-4 w-4" />
            Payment Method
          </TabsTrigger>
          <TabsTrigger value="invoices" className="text-xs sm:text-sm">
            <FileText className="mr-1.5 h-4 w-4" />
            Invoice History
          </TabsTrigger>
        </TabsList>

        {/* Usage Tab */}
        <TabsContent value="usage" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">Usage This Period</CardTitle>
              <CardDescription>Services rendered and their billable amounts</CardDescription>
            </CardHeader>
            <CardContent>
              {usageLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : !usageData?.events?.length ? (
                <p className="py-8 text-center text-sm text-slate-400">No usage events this period</p>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {(['mdi_visit', 'consulting_visit', 'lab_order', 'at_home_phlebotomy'] as const).map((type) => (
                      <div key={type} className="rounded-lg bg-slate-50 p-3 text-center">
                        <p className="text-xs text-slate-500">{EVENT_LABELS[type]}</p>
                        <p className="text-lg font-bold text-slate-900">{usageData.summary[type]}</p>
                      </div>
                    ))}
                  </div>

                  {/* Running Total */}
                  <div className="mb-4 flex items-center justify-between rounded-lg bg-brand-teal/5 px-4 py-3">
                    <span className="text-sm font-medium text-slate-700">Running Total</span>
                    <span className="text-lg font-bold text-brand-teal">
                      ${usageData.summary.totalAmount.toFixed(2)}
                    </span>
                  </div>

                  {/* Events Table */}
                  <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                          <tr className="border-b border-slate-200">
                            <th
                              className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                              style={{ fontSize: '0.7rem' }}
                            >
                              Date
                            </th>
                            <th
                              className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700"
                              style={{ fontSize: '0.7rem' }}
                            >
                              Service
                            </th>
                            <th
                              className="px-4 py-3 text-right font-semibold uppercase tracking-wider text-slate-700"
                              style={{ fontSize: '0.7rem' }}
                            >
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {usageData.events.slice(0, 20).map((event: UsageEvent) => (
                            <tr
                              key={event.id}
                              className="border-b border-slate-100 transition-colors hover:bg-slate-50/80"
                            >
                              <td className="px-4 py-3 text-slate-600" style={{ fontSize: '0.8rem' }}>
                                {new Date(event.created_at).toLocaleDateString()}{' '}
                                {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="px-4 py-3" style={{ fontSize: '0.8rem' }}>
                                <Badge variant="outline" className="text-xs">
                                  {EVENT_LABELS[event.event_type]}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900" style={{ fontSize: '0.8rem' }}>
                                ${Number(event.amount || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Method Tab */}
        <TabsContent value="payment" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">Payment Method</CardTitle>
              <CardDescription>Card on file for monthly billing</CardDescription>
            </CardHeader>
            <CardContent>
              {status?.paymentMethod ? (
                <div className="flex items-center justify-between rounded-lg bg-slate-50 p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-8 w-8 text-slate-400" />
                    <div>
                      <p className="font-medium capitalize text-slate-900">
                        {status.paymentMethod.brand}
                      </p>
                      <p className="text-sm text-slate-500">
                        •••• •••• •••• {status.paymentMethod.last4}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUpdateCard(true)}
                  >
                    Update
                  </Button>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <CreditCard className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                  <p className="text-sm text-slate-500">No payment method on file</p>
                  <Button
                    onClick={() => setShowSetup(true)}
                    className="mt-3 bg-brand-teal hover:bg-brand-teal/90"
                  >
                    Add Payment Method
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">Invoice History</CardTitle>
              <CardDescription>Monthly invoices from Stripe</CardDescription>
            </CardHeader>
            <CardContent>
              {invoicesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : !invoices?.length ? (
                <p className="py-8 text-center text-sm text-slate-400">No invoices yet</p>
              ) : (
                <div className="space-y-2">
                  {invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {invoice.number || invoice.id}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(invoice.created).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-900">
                            ${invoice.amountPaid.toFixed(2)}
                          </p>
                          <Badge
                            variant="outline"
                            className={`capitalize ${
                              invoice.status === 'paid'
                                ? 'border-emerald-200 text-emerald-600'
                                : 'border-amber-200 text-amber-600'
                            }`}
                          >
                            {invoice.status}
                          </Badge>
                        </div>
                        {invoice.hostedUrl && (
                          <a
                            href={invoice.hostedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-md p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        {invoice.pdfUrl && (
                          <a
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-md p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default BillingDashboard
